import { CellContext, ColumnDef } from '@tanstack/react-table'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { selectColor, StatusChip } from 'components/Chips/StatusChip'
import { DiffViewer } from 'components/DiffViewer'
import { LastModifiedCell } from 'components/Table'
import { COLUMN_SIZE } from 'components/Table/constants'
import { DATE_FORMAT } from 'utils/date'
import { BuildOrderStatus } from '../../types'
import { BuildOrderForTable } from '../useGetBuildOrders'
import { ActionsCell } from './components/ActionsCell'

export const useColumns = (): ColumnDef<BuildOrderForTable>[] => {
  const { t } = useTranslation(['features/history', 'common'])

  return [
    {
      accessorKey: 'updatedAt',
      cell: (props: CellContext<BuildOrderForTable, unknown>) => (
        <LastModifiedCell
          {...{ ...props, convertToUtc: true, dateFormat: DATE_FORMAT.WITH_TIME }}
        />
      ),
      header: t('builds.list.header.updatedAt'),
    },
    {
      accessorKey: 'domain.name',
      header: t('builds.list.header.domainName'),
    },
    {
      accessorKey: 'changes',
      cell: ({
        row: {
          original: { changes },
        },
      }: CellContext<BuildOrderForTable, unknown>): ReactNode =>
        changes ? (
          <DiffViewer
            modified={changes.modified}
            original={changes.original}
          />
        ) : (
          t('builds.list.noChanges')
        ),
      header: t('builds.list.header.changes.label'),
      meta: {
        tooltip: { content: t('builds.list.header.changes.tooltip') },
      },
    },
    {
      accessorKey: 'username',
      header: t('builds.list.header.username.label'),
      meta: {
        tooltip: { content: t('builds.list.header.username.tooltip') },
      },
    },
    {
      accessorKey: 'status',
      cell: ({
        row: {
          original: { status },
        },
      }: CellContext<BuildOrderForTable, unknown>) => (
        <StatusChip
          chipColors={theme => selectColor<BuildOrderStatus>(status, theme)}
          status={status}
        />
      ),
      header: t('builds.list.header.status'),
      meta: {
        nonClickable: true,
      },
    },
    {
      accessorKey: 'actions',
      cell: (props: CellContext<BuildOrderForTable, unknown>): React.JSX.Element => (
        <ActionsCell {...props.row.original} />
      ),
      header: t('builds.list.header.actions'),
      size: COLUMN_SIZE.SM,
    },
  ]
}
